import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import {
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
  useFormikContext,
} from 'formik';
import { useMedia } from 'tamagui';

import {
  Button,
  CheckboxListItem,
  Highlight,
  IconBannersWarning,
  HStack,
  LoadingModal,
  Stack,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import PreventSubmitWrapper from '../ui/forms/PreventSubmitWrapper';
import TextInputWithError from '../ui/forms/TextInputWithError';
import i18n from '../../utilities/i18n';
import Select, { SelectOption } from '../ui/forms/Select';
import log from '../../utilities/log';
import { useNavigationPrompt } from '../../contexts/NavigationPromptContext';

export enum DynamicFormFieldType {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  TEXT = 'text',
  PERCENT = 'percent',
  CHOICE = 'choice',
}

type BaseDynamicFormField = {
  name: string;
  label: string;
  isEditable?: boolean;
  isEditOnly?: boolean;
};

type DynamicFormField =
  | (BaseDynamicFormField & {
      type:
        | DynamicFormFieldType.DATE
        | DynamicFormFieldType.TEXT
        | DynamicFormFieldType.PERCENT;
    })
  | (BaseDynamicFormField & {
      type: DynamicFormFieldType.CHECKBOX;
      checkboxOptions: {
        checkedLabel: string;
        uncheckedLabel: string;
      };
    })
  | (BaseDynamicFormField & {
      type: DynamicFormFieldType.CHOICE;
      choiceOptions: SelectOption[];
    });

export type DynamicFormConfig<T> = {
  fields: DynamicFormField[];
  validationSchema: Record<keyof T, Yup.AnySchema>;
  initialValues: T;
};

type DynamicFormProps<T> = {
  id: string;
  title: string;
  formConfig: DynamicFormConfig<T>;
  onSave: (values: Partial<T>) => Promise<T>;
  maxRows?: number;
  children?: React.ReactNode;
};

type DynamicFormDirtyObserverProps = {
  isEditEnabled: boolean;
};

function DynamicFormDirtyObserver<T extends Record<string, any>>({
  isEditEnabled,
}: DynamicFormDirtyObserverProps) {
  const formikProps = useFormikContext<T>();
  const { setIsFormDirty } = useNavigationPrompt();

  useEffect(() => {
    setIsFormDirty(
      isEditEnabled && formikProps.dirty && !formikProps.isSubmitting,
    );
  }, [
    formikProps.dirty,
    formikProps.isSubmitting,
    isEditEnabled,
    setIsFormDirty,
  ]);

  useEffect(() => {
    return () => setIsFormDirty(false);
  }, [setIsFormDirty]);

  return null;
}

function getDirtyFields<T extends Record<string, any>>(
  initialValues: T,
  currentValues: T,
): Partial<T> {
  return Object.keys(currentValues).reduce((acc, key) => {
    if (initialValues[key] !== currentValues[key]) {
      acc[key] = currentValues[key];
    }

    return acc;
  }, {});
}

/**
 * A dynamic form component that renders a form based on the provided configuration.
 * It supports editing and validation of form fields, and allows for a customizable number of rows.
 * Children button components can be passed to the form for additional header actions.
 * @template T - Type of the form values. Must be key-value pairs where keys are strings.
 * @param {string} id - Unique identifier for the form
 * @param {string} title - Title of the form
 * @param {DynamicFormConfig} formConfig - Configuration for the form fields and validation
 * @param {(values: T) => Promise<T>} onSave - Callback function to handle form submission
 * @param {number} maxRows - Number of rows to display in the form (default is the number of fields)
 * @param {React.ReactNode} children - Optional children components to render within the form header in view mode
 * @returns {React.JSX.Element}
 */
export default function DynamicForm<T extends Record<string, any>>({
  id,
  title,
  formConfig,
  onSave,
  maxRows = formConfig.fields.length,
  children,
}: DynamicFormProps<T>) {
  const media = useMedia();
  const { setIsFormDirty } = useNavigationPrompt();

  const numColumns = Math.ceil(formConfig.fields.length / maxRows);

  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [initialValues, setInitialValues] = useState(formConfig.initialValues);

  const onEditEnable = () => {
    setIsEditEnabled(true);
  };

  const onEditCancel = (helpers: FormikHelpers<T>) => {
    helpers.setStatus('');
    helpers.resetForm();
    setIsFormDirty(false);

    setIsEditEnabled(false);
  };

  const onSubmit = async (values: T, helpers: FormikHelpers<T>) => {
    const stepErrors = await helpers.validateForm();

    if (Object.keys(stepErrors).length === 0) {
      try {
        const dirtyFields = getDirtyFields<T>(initialValues, values);

        const updatedValues = await onSave(dirtyFields);
        const updatedInitialValues = { ...initialValues, ...updatedValues };

        setInitialValues(updatedInitialValues);

        helpers.setStatus('');
        helpers.resetForm();
        setIsFormDirty(false);

        setIsEditEnabled(false);

        return true;
      } catch (error) {
        helpers.setStatus({ error });
        console.error(`Error saving dynamic form ${id}:`, error);
      }
    }

    return false;
  };

  const renderFormFieldValue = (
    field: DynamicFormField,
    formikProps: FormikProps<T>,
  ) => {
    // List of values that are considered "no data"
    const isNoDataValue =
      formikProps.values[field.name] === undefined ||
      formikProps.values[field.name] === null ||
      formikProps.values[field.name] === '' ||
      (field.type === DynamicFormFieldType.DATE &&
        formikProps.values[field.name] === 0);
    let fieldValue = i18n.t('Common.noData');

    if (!isNoDataValue) {
      switch (field.type) {
        case DynamicFormFieldType.DATE:
          fieldValue = new Date(
            formikProps.values[field.name],
          ).toLocaleString();
          break;
        case DynamicFormFieldType.CHECKBOX:
          fieldValue =
            formikProps.values[field.name] === true
              ? field.checkboxOptions.checkedLabel
              : field.checkboxOptions.uncheckedLabel;
          break;
        case DynamicFormFieldType.CHOICE:
          fieldValue =
            field.choiceOptions?.find(
              (option) => option.id === String(formikProps.values[field.name]),
            )?.name || formikProps.values[field.name];
          break;
        case DynamicFormFieldType.PERCENT:
          fieldValue = `${formikProps.values[field.name]}%`;
          break;
        case DynamicFormFieldType.TEXT:
        default:
          fieldValue = formikProps.values[field.name];
      }
    }

    return (
      <Text
        type={'$bodyBase.default'}
        color={isNoDataValue ? '$text.60' : '$text.100'}
        textAlign="left"
        padding={'$4'}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        testID={`${field.name}-value`}
      >
        {fieldValue}
      </Text>
    );
  };

  const renderFormFieldInput = (
    field: DynamicFormField,
    formikProps: FormikProps<T>,
  ) => {
    switch (field.type) {
      case DynamicFormFieldType.CHECKBOX:
        return (
          <PreventSubmitWrapper>
            <HStack marginVertical={'$1'}>
              <CheckboxListItem
                title={
                  formikProps.values[field.name] === true
                    ? field.checkboxOptions.checkedLabel
                    : field.checkboxOptions.uncheckedLabel
                }
                checked={formikProps.values[field.name]}
                onPress={(checked) => {
                  formikProps.setFieldValue(field.name, checked);
                }}
                disabled={
                  !isEditEnabled ||
                  !field.isEditable ||
                  formikProps.isSubmitting
                }
                testID={field.name}
              />
            </HStack>
          </PreventSubmitWrapper>
        );
      case DynamicFormFieldType.CHOICE:
        return (
          <HStack marginBottom="$2">
            <Select
              name={field.name}
              id={field.name}
              key={field.name}
              aria-label={field.label}
              options={field.choiceOptions}
              label={null}
              disabled={
                !isEditEnabled || !field.isEditable || formikProps.isSubmitting
              }
              testID={field.name}
            />
          </HStack>
        );
      case DynamicFormFieldType.PERCENT:
        return (
          <TextInputWithError
            key={field.name}
            name={field.name}
            label={null}
            aria-label={field.label}
            disabled={
              !isEditEnabled || !field.isEditable || formikProps.isSubmitting
            }
            inlineLabel="%"
            testID={field.name}
          />
        );
      case DynamicFormFieldType.TEXT:
      default:
        return (
          <TextInputWithError
            key={field.name}
            name={field.name}
            label={null}
            aria-label={field.label}
            disabled={
              !isEditEnabled || !field.isEditable || formikProps.isSubmitting
            }
            testID={field.name}
          />
        );
    }
  };

  return (
    <VStack id="dynamic-form-container">
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object().shape(formConfig.validationSchema)}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {(formikProps: FormikProps<T>) => (
          <Form id={`${id}-dynamic-form`} noValidate>
            <DynamicFormDirtyObserver<T> isEditEnabled={isEditEnabled} />
            <VStack gap="$4">
              {formikProps.status?.error ? (
                <Highlight
                  variant="danger"
                  iconStart={
                    <IconBannersWarning
                      color="$neutral.0"
                      accentColor="$support.danger.default"
                    />
                  }
                  title={i18n.t('Common.errorSaving')}
                  body={formikProps.status.error.error}
                  testID={`${id}-dynamic-form-error-banner`}
                />
              ) : null}
              <Stack
                justifyContent="space-between"
                alignItems={media.sm ? 'flex-start' : 'center'}
                flexDirection={media.sm ? 'column' : 'row'}
                gap={media.sm ? '$2' : 0}
              >
                <Text type="$h4.strong">{title}</Text>
                <HStack alignItems="center" gap="$2">
                  {isEditEnabled ? (
                    <>
                      <Button
                        variant="secondary"
                        size="medium"
                        label={i18n.t('Common.cancel')}
                        onPress={() => onEditCancel(formikProps)}
                        testID={`${id}-dynamic-form-cancel-button`}
                        disabled={formikProps.isSubmitting}
                      />
                      <Button
                        variant="primary"
                        size="medium"
                        onPress={() => {
                          log.debug(
                            'Submitting dynamic form with formikProps:',
                            formikProps,
                          );
                          formikProps.submitForm();
                        }}
                        label={i18n.t('Common.save')}
                        disabled={
                          formikProps.isSubmitting || !formikProps.dirty
                        }
                        testID={`${id}-dynamic-form-submit-button`}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        variant="primary"
                        size="medium"
                        label={i18n.t('Common.edit')}
                        onPress={onEditEnable}
                        testID={`${id}-dynamic-form-edit-button`}
                      />
                      {children}
                    </>
                  )}
                </HStack>
              </Stack>
              <Stack
                className="dynamic-form-grid-container"
                flexDirection={media.sm ? 'column' : 'row'}
                gap={media.sm ? 0 : '$10'}
              >
                {formikProps.isSubmitting && (
                  <LoadingModal
                    aria-label={i18n.t('Common.loading')}
                    testID="dynamic-form-loading-modal"
                  />
                )}
                {Array.from({ length: numColumns }).map((_, columnIndex) => (
                  <div
                    className="dynamic-form-grid"
                    key={`column-${columnIndex}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: media.sm ? '1fr' : '1fr 3fr',
                      textAlign: 'left',
                      flex: 1,
                      height: '100%',
                      rowGap: 'var(--size-xs)',
                    }}
                  >
                    {formConfig.fields
                      .slice(columnIndex * maxRows, (columnIndex + 1) * maxRows)
                      .map((field) => (
                        <React.Fragment key={field.name}>
                          {!isEditEnabled && field.isEditOnly ? null : (
                            <>
                              <span
                                title={field.label}
                                style={{
                                  display: 'flex',
                                  height: 'max-content',
                                }}
                              >
                                <Text
                                  className="dynamic-form-grid-field-label"
                                  type={'$bodyBase.strong'}
                                  alignContent="center"
                                  marginRight={'$2'}
                                  whiteSpace="nowrap"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  testID={`${field.name}-label`}
                                  height="100%"
                                  paddingVertical="$4"
                                >
                                  {field.label}
                                </Text>
                              </span>

                              {isEditEnabled && field.isEditable
                                ? renderFormFieldInput(field, formikProps)
                                : renderFormFieldValue(field, formikProps)}
                            </>
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                ))}
              </Stack>
            </VStack>
          </Form>
        )}
      </Formik>
    </VStack>
  );
}
